import { useQuery } from '@tanstack/react-query';
import { VersionMismatchBadge } from '@thedealersconcierge/components/src/badge/versionMismatch';
import useOfflineNotification from '@thedealersconcierge/components/src/hooks/useOfflineNotification';
import { platformVersion } from '@thedealersconcierge/lib/platform-version';
import { useAtomValue } from 'jotai';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, useRouteError } from 'react-router';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorFallback from '~/components/ErrorFallback';
import config from '~/config';
import { gqlQueryClient } from '~/lib/backend';
import { authStateAtom, dealershipAtom } from '~/state';

const LoadAtoms = () => {
  useAtomValue(authStateAtom);
  useAtomValue(dealershipAtom);

  return null;
};

/**
 * This hook changes the fav icon in accordance with the environment we are
 * using.
 */
const FavIconHook = () => {
  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    if (config.prodEnv) {
      link.href = `/logo-${config.prodEnv}.png`;
    } else if (!config.isProduction) {
      link.href = `/logo-local.png`;
    }
  }, []);

  return null;
};

/**
 * Route-level error boundary picked up by generouted (`Catch` export on _app).
 * Renders for errors thrown in loaders, lazy chunk loads, and route render.
 */
export const Catch = () => {
  const error = useRouteError();
  return <ErrorFallback error={error} />;
};

export default function App() {
  useOfflineNotification(config.rawBackendUrl);

  // Version mismatch check, it is intended to not be wrapped in other function / hook wrapper
  // As it is only used here, reducing unnecessary indirection
  const { data } = useQuery({
    queryKey: ['platform-version'],
    refetchInterval: 60000, // Refetch every 60 sec, this might change
    refetchOnWindowFocus: true, // Ensure we also refetch on focus
    queryFn: () => gqlQueryClient()({ public: { platformVersion: true } })
  });

  // Triggered when frontend is running an outdated version compared to backend
  const backendVersion = data?.public?.platformVersion;
  if (backendVersion && backendVersion !== platformVersion) {
    toast(<VersionMismatchBadge />, {
      autoClose: false,
      closeButton: false,
      toastId: 'version-mismatch-badge',
      closeOnClick: false,
      style: { maxWidth: '380px' }
    });
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }: { error: unknown }) => (
        <ErrorFallback error={error} />
      )}
      onReset={() => {
        window.location.href = '/';
      }}
      resetKeys={['someKey']}
    >
      {/* Root Suspense */}
      <Suspense fallback={<div>Loading app</div>}>
        {/* Load atoms first */}
        <LoadAtoms />

        {/* Then do the rest */}
        <FavIconHook />

        <Outlet />

        <ToastContainer position="bottom-right" />
      </Suspense>
    </ErrorBoundary>
  );
}
